import { keysToSnakeCase } from "../../utils/keys-to-snake-case";
import { pushClearEcommerceEvent, pushGtmEvent, type UnknownRecord } from "./create-event";
import { sanitizeItemName } from "./utils/sanitize-item-name";

export type ItemListId = "listing" | "wyniki_wyszukiwania" | "upsell";

type Product = {
  uuid: string;
  name: string;
  productType: "blood-test" | "blood-test-package";
  grossPrice: string;
  regularPrice?: string;
  quantity: number;
  isUpsell?: boolean;
  item_list_id?: ItemListId;
  index?: number;
};

type EcommerceEventOptions = {
  includeIndex?: boolean;
  includeIsUpsell?: boolean;
};

// Guard every monetary field emitted to GA4 against floating-point noise
// (e.g. 12.99 * 3 → 38.97000000000001). Round to 2 decimal places.
const roundPrice = (n: number): number => Math.round(n * 100) / 100;

const sanitizeEcomData = (data: UnknownRecord | undefined): UnknownRecord | undefined => {
  if (!data) return data;
  const copy: UnknownRecord = { ...data };
  if (typeof copy.value === "number") copy.value = roundPrice(copy.value);
  return copy;
};

export const createEcommerceEvent = <
  EcomData extends UnknownRecord | undefined = undefined,
  RootData extends UnknownRecord | undefined = undefined,
>(
  event: string,
  defaultArgs: UnknownRecord = {},
  options: EcommerceEventOptions = {},
) => {
  const { includeIndex = true, includeIsUpsell = false } = options;

  return (
    products: Product[],
    ...args: EcomData extends undefined
      ? RootData extends undefined
        ? []
        : [undefined, RootData]
      : RootData extends undefined
        ? [EcomData]
        : [EcomData, RootData]
  ) => {
    const ecomData = args[0] as EcomData | undefined;
    const rootData = args[1] as RootData | undefined;

    const firstProduct = products[0];

    pushClearEcommerceEvent();
    pushGtmEvent({
      event,
      ...keysToSnakeCase(rootData as UnknownRecord | undefined),
      site_area: "b2b",
      ...(includeIsUpsell && firstProduct?.isUpsell !== undefined && { product_is_upsell: firstProduct.isUpsell }),
      ecommerce: {
        currency: "PLN",
        items: products.map((product, idx) => ({
          item_id: product.uuid,
          // REV-31: normalize at the single emission point so the same item_id ships an identical
          // item_name across site_area b2c/b2b (GA4 stops splitting one product into several rows).
          item_name: sanitizeItemName(product.name),
          // REV-165: clamp to >= 0. Some products carry a `regularPrice` that is *lower* than
          // `grossPrice` (legacy/default field set on the BE side); the catalog UI hides the
          // "regular price" line for those, so to GA4 they're not on sale and `discount` must be
          // 0 — not a negative "savings".
          discount: Math.max(
            0,
            roundPrice(Number(product.regularPrice || product.grossPrice) - Number(product.grossPrice)),
          ),
          item_brand: "uPacjenta",
          item_category: product.productType,
          price: roundPrice(Number(product.grossPrice)),
          quantity: product.quantity,
          ...(includeIndex && { index: product.index ?? idx + 1 }),
          ...(product.item_list_id && { item_list_id: product.item_list_id }),
        })),
        ...keysToSnakeCase(defaultArgs),
        ...keysToSnakeCase(sanitizeEcomData(ecomData as UnknownRecord | undefined)),
      },
    });
  };
};
